import { forwardRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Expandable } from '@cb/apricot-react';
import { Grid } from '@mui/material';
import { styled } from '@mui/system';

import { COLORS } from 'src/constants';
import { TFormGroup } from 'src/types';

import FormField from './FormField';
import Typography from './Typography';

const StyledGrid = styled(Grid)`
  padding: 8px 0;
  border-bottom: 1px solid ${COLORS.borderNavy};

  &:first-of-type {
    border-top: 1px solid ${COLORS.borderNavy};
  }

  &:nth-of-type(odd) {
    background: ${COLORS.ivory};
  }
`;

export interface FormGroupProps {
  group: TFormGroup;
  isReadOnly: boolean;
  showManualError?: boolean;
  onUploadFile?: (file: File, description: string, path?: string) => Promise<Nullable<string>>;
}

const FormGroup = forwardRef<HTMLDivElement, FormGroupProps>(
  ({ group, isReadOnly, showManualError, onUploadFile }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const isRequired = group.isExpandable ? isOpen : true;

    const { getValues } = useFormContext();
    const values = getValues();

    const fieldsToRender = group.fields.filter((field) => !field.renderCondition || field.renderCondition(values));

    if (group.renderCondition && group.renderCondition(values) === false) {
      return null;
    }

    const content = (
      <Grid container spacing={2}>
        {group.showGroupName && (
          <Grid item xs={12}>
            <Typography variant="body1">{group.groupName}</Typography>
          </Grid>
        )}

        {fieldsToRender.map((field) => (
          <Grid key={field.path} item xs={field.xs} lg={field.lg}>
            <FormField
              field={field}
              showManualError={showManualError}
              variant="outlined"
              required={isRequired}
              readOnly={isReadOnly}
              onUploadFile={onUploadFile}
            />
          </Grid>
        ))}
      </Grid>
    );

    return (
      <StyledGrid item xs={12} ref={ref}>
        {group.isExpandable ? (
          // @ts-ignore
          <Expandable openLabel={group.groupName} closedLabel={group.groupName} onChange={setIsOpen}>
            {content}
          </Expandable>
        ) : (
          content
        )}
      </StyledGrid>
    );
  },
);

FormGroup.displayName = 'FormGroup';

export default FormGroup;
